/**
 * SB2-W-16 — Projects list column definitions.
 *
 * Order locked per Christian's brief in `03-page_projects.md` §Table:
 *   1) Project Code (BIGINT, sortable; default sort DESC)
 *   2) Name         (string, sortable)
 *   3) Start Date   (date-time, sortable)
 *   4) Customer     (string, sortable)
 *   5) Contractor   (string, sortable)
 *   6) Total Assets (int, sortable, right-aligned)
 *   7) Progress     (int 0..100, sortable, bar)
 *   8) Status       (enum chip, color-coded)
 *   9) Workflow     (PM-WF-01: label, or the "Workflow not set" badge)
 *  10) Edit         (pencil + "edit" label, NOT sortable)
 */
import { Box, Button, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ProjectStatusChip } from "./ProjectStatusChip.js";
import { ProjectProgressBar } from "./ProjectProgressBar.js";
import { WorkflowNotSetChip } from "./WorkflowNotSetChip.js";
import {
  ownershipColumn as sharedOwnershipColumn,
  type OwnershipRow,
} from "../shared/ownership/index.js";
import type { CustomerProjectRow } from "./access/customerProjection.js";
import type { ProjectListItem } from "./types.js";
import { PencilIcon } from "@aeriosiq/ui/icons";

/** TNCY-6 §c — the ownership-icon column (`<OwnershipBadge>`), prepended to
 *  the merged owner/member list. Promoted to `features/shared/ownership` in
 *  TNCY-6b; this thin wrapper pins the project-specific owner tooltip noun
 *  so existing `features/projects/columns` importers keep their path. */
export function ownershipColumn<R extends OwnershipRow>(): GridColDef<R> {
  return sharedOwnershipColumn<R>({ entityNoun: "project" });
}

function formatStartDate(raw: string | null): string {
  if (!raw) return "—";
  // BE emits a calendar-only YYYY-MM-DD; render that verbatim. If a
  // future contract bump returns a full date-time we still handle it.
  const calendarOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  if (calendarOnly) return raw;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toISOString().slice(0, 10);
}

export function buildProjectColumns(args: {
  onEdit: (row: ProjectListItem) => void;
}): GridColDef<ProjectListItem>[] {
  const { onEdit } = args;
  return [
    {
      field: "projectCode",
      headerName: "Project Code",
      sortable: true,
      flex: 0.6,
      minWidth: 110,
      // SB4-05 / decision L2 — Project Code is left-aligned. Christian
      // at [00:16:40]: "the first column is project code here the text
      // is aligned on the right side it should be on the left side...
      // alignment should be always on the left side".
      align: "left",
      headerAlign: "left",
      renderCell: (p: GridRenderCellParams<ProjectListItem, number>) => (
        <Box
          sx={{
            fontVariantNumeric: "tabular-nums",
            width: "100%",
            textAlign: "left",
          }}
        >
          {p.row.projectCode}
        </Box>
      ),
    },
    {
      field: "projectName",
      headerName: "Name",
      sortable: true,
      flex: 1.2,
      minWidth: 160,
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.projectName}
        </Typography>
      ),
    },
    {
      field: "startDate",
      headerName: "Start Date",
      sortable: true,
      flex: 0.7,
      minWidth: 120,
      renderCell: (p) => (
        <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
          {formatStartDate(p.row.startDate)}
        </Box>
      ),
    },
    {
      field: "customerName",
      headerName: "Customer",
      sortable: true,
      flex: 0.9,
      minWidth: 140,
      // GridColDef.field is the sort-by key on the wire (we translate
      // to the BE's `customerName` sortBy enum value in the page); the
      // value renderer reads the nested customerTenantName instead.
      valueGetter: (_v, row: ProjectListItem) => row.customerTenantName ?? "",
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.customerTenantName ?? "—"}
        </Typography>
      ),
    },
    {
      field: "contractorName",
      headerName: "Contractor",
      sortable: true,
      flex: 0.9,
      minWidth: 140,
      valueGetter: (_v, row: ProjectListItem) => row.contractorTenantName ?? "",
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.contractorTenantName ?? "—"}
        </Typography>
      ),
    },
    {
      field: "totalAssets",
      headerName: "Total Assets",
      sortable: true,
      flex: 0.5,
      minWidth: 100,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
          {p.row.totalAssets}
        </Box>
      ),
    },
    {
      field: "progressPct",
      headerName: "Progress",
      sortable: true,
      flex: 0.8,
      minWidth: 140,
      renderCell: (p) => <ProjectProgressBar value={p.row.progressPct} />,
    },
    {
      field: "state",
      headerName: "Status",
      sortable: true,
      flex: 0.7,
      minWidth: 130,
      renderCell: (p) => <ProjectStatusChip state={p.row.state} />,
    },
    {
      // PM-WF-01 (projects/v1 2.25.0) — the project's workflow; the "not set"
      // badge on own rows (a member row's workflow is its owner's setting).
      field: "workflow",
      headerName: "Workflow",
      sortable: false,
      flex: 0.9,
      minWidth: 150,
      renderCell: (p) =>
        p.row.workflow !== null ? (
          <Typography variant="body2" noWrap title={p.row.workflow}>
            {p.row.workflowLabel ?? p.row.workflow}
          </Typography>
        ) : p.row.ownership === "member" ? (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ) : (
          <WorkflowNotSetChip />
        ),
    },
    {
      field: "__edit",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 96,
      align: "left",
      headerAlign: "left",
      renderCell: (p) => (
        <Button
          size="small"
          variant="text"
          startIcon={<PencilIcon fontSize="small" />}
          aria-label={`Edit project ${p.row.projectName}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(p.row);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];
}

/**
 * TNCY-6 §d — customer read projection columns (XT-D21/D22, AC-4).
 *
 * The outcome surface only: code, name, start, total assets, progress,
 * status. The **Contractor column is dropped** (customers never see the
 * chain) — and because the rows are `CustomerProjectRow` (which physically
 * omits the chain fields), even a stray renderer can't reach them. No Edit
 * column (customers have no write). Customer column is kept for context.
 */
export function buildCustomerProjectColumns(): GridColDef<CustomerProjectRow>[] {
  return [
    {
      field: "projectCode",
      headerName: "Project Code",
      sortable: true,
      flex: 0.6,
      minWidth: 110,
      align: "left",
      headerAlign: "left",
      renderCell: (p: GridRenderCellParams<CustomerProjectRow, number>) => (
        <Box
          sx={{
            fontVariantNumeric: "tabular-nums",
            width: "100%",
            textAlign: "left",
          }}
        >
          {p.row.projectCode}
        </Box>
      ),
    },
    {
      field: "projectName",
      headerName: "Name",
      sortable: true,
      flex: 1.2,
      minWidth: 160,
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.projectName}
        </Typography>
      ),
    },
    {
      field: "startDate",
      headerName: "Start Date",
      sortable: true,
      flex: 0.7,
      minWidth: 120,
      renderCell: (p) => (
        <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
          {formatStartDate(p.row.startDate)}
        </Box>
      ),
    },
    {
      field: "totalAssets",
      headerName: "Total Assets",
      sortable: true,
      flex: 0.5,
      minWidth: 100,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
          {p.row.totalAssets}
        </Box>
      ),
    },
    {
      field: "progressPct",
      headerName: "Progress",
      sortable: true,
      flex: 0.8,
      minWidth: 140,
      renderCell: (p) => <ProjectProgressBar value={p.row.progressPct} />,
    },
    {
      field: "state",
      headerName: "Status",
      sortable: true,
      flex: 0.7,
      minWidth: 130,
      renderCell: (p) => <ProjectStatusChip state={p.row.state} />,
    },
  ];
}
